import React, { useMemo } from "react";
import { Space, Table, Tag, Typography } from "antd";
import type { CheckboxProps, TableColumnsType } from "antd";
import type { DownloadCourse, DownloadParts } from "./downloads.types";
import { formatLastDownload, formatLastUpload } from "./formatLastDownload";
import { describeRow } from "./rowResult";
import type { DownloadRowState } from "./rowResult";
import "./CoursesTable.css";

interface Item {
  course: DownloadCourse;
  // Set once a run has the course.
  state: DownloadRowState | undefined;
}

export const MONO: React.CSSProperties = {
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: 14,
};

// A course with a standalone quiz and a video quiz (SCRUM-1531).
const DOUBLE_QUIZ_ROW = "hub-downloads-double-quiz";
const DOUBLE_QUIZ_WARNING = "⚠ Tiene quiz suelto y video-quiz: elimina uno";

const PILL: React.CSSProperties = { margin: 0 };
const OUTLINED_PILL: React.CSSProperties = {
  ...PILL,
  background: "var(--bo-card-bg)",
};

const Availability: React.FC<{ available: boolean }> = ({ available }) => (
  <Tag color={available ? "success" : "default"} style={PILL}>
    {available ? "Sí" : "No hay"}
  </Tag>
);

// A part that is not being downloaded, or a value the backend did not send.
const Dash: React.FC = () => (
  <Typography.Text type="secondary">—</Typography.Text>
);

// A backend that predates a field leaves it out: «—», never a guess.
const YesNo: React.FC<{ value: boolean | undefined }> = ({ value }) => {
  if (typeof value !== "boolean") return <Dash />;
  return value ? (
    <Tag color="success" style={PILL}>
      Sí
    </Tag>
  ) : (
    <Tag style={OUTLINED_PILL}>No</Tag>
  );
};

const Synced: React.FC<{ synced: boolean | null | undefined }> = ({
  synced,
}) => {
  if (typeof synced !== "boolean") return <Dash />;
  return (
    <Tag color={synced ? "success" : "warning"} style={PILL}>
      {synced ? "Sí" : "No"}
    </Tag>
  );
};

const CourseTitle: React.FC<{ course: DownloadCourse }> = ({ course }) =>
  course.doubleQuizError ? (
    <Space direction="vertical" size={2}>
      <span>{course.title}</span>
      <Typography.Text type="warning" style={{ fontSize: 12 }}>
        {DOUBLE_QUIZ_WARNING}
      </Typography.Text>
    </Space>
  ) : (
    <>{course.title}</>
  );

const RowStatus: React.FC<{ state: DownloadRowState }> = ({ state }) => {
  const { label, color } = describeRow(state);
  return (
    <Space direction="vertical" size={2}>
      <Tag color={color} style={{ margin: 0, whiteSpace: "normal" }}>
        {label}
      </Tag>
      {state.kind === "running" && state.note ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {state.note}
        </Typography.Text>
      ) : null}
      {state.kind === "done"
        ? state.warnings.map((warning) => (
            <Typography.Text
              key={warning}
              type="warning"
              style={{ fontSize: 12 }}
            >
              {warning}
            </Typography.Text>
          ))
        : null}
    </Space>
  );
};

const termAndWeek = ({ trimester, week }: DownloadCourse): string =>
  `${trimester === null ? "—" : `T${trimester}`} · ${week ?? "—"}`;

const PART_COLUMNS = ["od", "quiz", "quizVideo"];

function buildColumns(
  parts: DownloadParts,
  showState: boolean,
): TableColumnsType<Item> {
  const columns: TableColumnsType<Item> = [
    {
      title: "Clave",
      key: "clave",
      render: (_, { course }) => (
        <span style={MONO}>{course.clave ?? course.courseId}</span>
      ),
    },
    {
      title: "Curso",
      key: "title",
      render: (_, { course }) => <CourseTitle course={course} />,
    },
    {
      title: "Grado",
      key: "grade",
      render: (_, { course }) => course.gradeLabel,
    },
    {
      title: "Asignatura",
      key: "subject",
      render: (_, { course }) => course.subjectName,
    },
    {
      title: "T · Sem",
      key: "term",
      render: (_, { course }) => termAndWeek(course),
    },
    {
      title: "OD",
      key: "od",
      render: (_, { course }) =>
        parts.od ? <Availability available={course.hasOd} /> : <Dash />,
    },
    {
      title: "Quiz",
      key: "quiz",
      render: (_, { course }) =>
        parts.quiz ? (
          <Availability available={course.quizCount > 0} />
        ) : (
          <Dash />
        ),
    },
    {
      title: "Quiz de video",
      key: "quizVideo",
      render: (_, { course }) =>
        parts.quizVideo ? <YesNo value={course.hasVideoQuiz} /> : <Dash />,
    },
    {
      title: "Diapo",
      key: "slides",
      render: (_, { course }) => <YesNo value={course.hasSlides} />,
    },
    {
      title: "Juego",
      key: "game",
      render: (_, { course }) => <YesNo value={course.hasGame} />,
    },
    {
      title: "Última subida",
      key: "lastUpload",
      render: (_, { course }) => (
        <Typography.Text type="secondary">
          {formatLastUpload(course.lastUploadAt)}
        </Typography.Text>
      ),
    },
    {
      title: showState ? "Estado" : "Última descarga",
      key: "last",
      render: (_, { course, state }) =>
        state ? (
          <RowStatus state={state} />
        ) : (
          <Typography.Text type="secondary">
            {formatLastDownload(course.lastDownloadAt)}
          </Typography.Text>
        ),
    },
    {
      title: "Sincronizado",
      key: "synced",
      render: (_, { course }) => <Synced synced={course.synced} />,
    },
  ];
  // An OD resumida has no OD, quiz or video quiz to show.
  return parts.odSummary
    ? columns.filter(({ key }) => !PART_COLUMNS.includes(String(key)))
    : columns;
}

interface Props {
  courses: DownloadCourse[];
  // What each course of the run is doing; empty before a run.
  rows: Record<number, DownloadRowState>;
  parts: DownloadParts;
  selectedIds: number[];
  // A run has the courses: they can no longer be marked or cleared.
  locked: boolean;
  onSelect: (ids: number[]) => void;
}

const CoursesTable: React.FC<Props> = ({
  courses,
  rows,
  parts,
  selectedIds,
  locked,
  onSelect,
}) => {
  const items = useMemo<Item[]>(
    () => courses.map((course) => ({ course, state: rows[course.courseId] })),
    [courses, rows],
  );
  const showState = Object.keys(rows).length > 0;
  const columns = useMemo(
    () => buildColumns(parts, showState),
    [parts, showState],
  );

  return (
    <Table<Item>
      rowKey={({ course }) => course.courseId}
      columns={columns}
      dataSource={items}
      pagination={false}
      scroll={{ x: 1400 }}
      rowClassName={({ course }) =>
        course.doubleQuizError ? DOUBLE_QUIZ_ROW : ""
      }
      locale={{ emptyText: "Ningún curso cumple esos filtros." }}
      rowSelection={{
        selectedRowKeys: selectedIds,
        onChange: (keys) => onSelect(keys as number[]),
        getCheckboxProps: ({ course }) =>
          ({
            disabled: locked,
            "aria-label": `Marcar ${course.clave ?? course.courseId}`,
          }) as Partial<CheckboxProps>,
        // antd words this one in English.
        columnTitle: (checkbox) =>
          React.isValidElement(checkbox)
            ? React.cloneElement(
                checkbox as React.ReactElement<{ "aria-label": string }>,
                { "aria-label": "Marcar todos" },
              )
            : checkbox,
      }}
    />
  );
};

export default CoursesTable;
